import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { AITaskProvider } from "./lib/aiTasks";
import { ProtectedRoute } from "./lib/ProtectedRoute";

import LoginPage from "./pages/LoginPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import AdminCompaniesPage from "./pages/AdminCompaniesPage";
import AdminBillingPage from "./pages/AdminBillingPage";
import CompanyBillingPage from "./pages/CompanyBillingPage";
import CompanyDetailPage from "./pages/CompanyDetailPage";
import JobCoachDashboardPage from "./pages/JobCoachDashboardPage";
import CoacheeDetailPage from "./pages/CoacheeDetailPage";
import CoacheeCVPage from "./pages/CoacheeCVPage";
import CoacheeTestsPage from "./pages/CoacheeTestsPage";
import CoacheeTestPlaceholderPage from "./pages/CoacheeTestPlaceholderPage";
import CoacheeIQTestPage from "./pages/CoacheeIQTestPage";
import CoacheeWorkValuesPage from "./pages/CoacheeWorkValuesPage";
import CoacheeSearchProfilePage from "./pages/CoacheeSearchProfilePage";
import CoacheeEmployerProfilePage from "./pages/CoacheeEmployerProfilePage";
import CoacheeEmployeeProfilePage from "./pages/CoacheeEmployeeProfilePage";
import CoacheeLinkedinPlanPage from "./pages/CoacheeLinkedinPlanPage";
import CoacheeVacaturesPage from "./pages/CoacheeVacaturesPage";
import CoacheeProfilesPage from "./pages/CoacheeProfilesPage";
import CoacheeBrainTypeProfilePage from "./pages/CoacheeBrainTypeProfilePage";
import CoacheeProfilePlaceholderPage from "./pages/CoacheeProfilePlaceholderPage";
import CoacheeCoverLetterPage from "./pages/CoacheeCoverLetterPage";
import CoacheeApplicationsListPage from "./pages/CoacheeApplicationsListPage";
import CoacheeInterviewPage from "./pages/CoacheeInterviewPage";
import CoacheeConflictTestPage from "./pages/CoacheeConflictTestPage";
import CoacheeCompetencePage from "./pages/CoacheeCompetencePage";
import CoacheeCareerValuesPage from "./pages/CoacheeCareerValuesPage";
import CoacheeProfessionPage from "./pages/CoacheeProfessionPage";
import CompetenceFeedbackPage from "./pages/CompetenceFeedbackPage";
import SurveyPage from "./pages/SurveyPage";
import JobCoachSettingsPage from "./pages/JobCoachSettingsPage";
import HelpPage from "./pages/HelpPage";
import PrivacyStatementPage from "./pages/PrivacyStatementPage";
import MyPrivacyPage from "./pages/MyPrivacyPage";
import DeletionRequestsPage from "./pages/DeletionRequestsPage";

function RoleRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "job_coach") return <Navigate to="/dashboard" replace />;
  return <Navigate to="/tests" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AITaskProvider>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyStatementPage />} />
            <Route
              path="/feedback/competentie/:token"
              element={<CompetenceFeedbackPage />}
            />
            <Route path="/" element={<RoleRedirect />} />

            <Route
              path="/account"
              element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>}
            />

            <Route
              path="/admin"
              element={<ProtectedRoute roles={["admin"]}><AdminCompaniesPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/facturatie"
              element={<ProtectedRoute roles={["admin"]}><AdminBillingPage /></ProtectedRoute>}
            />
            <Route
              path="/companies/:companyId"
              element={<ProtectedRoute roles={["admin", "job_coach"]}><CompanyDetailPage /></ProtectedRoute>}
            />
            <Route
              path="/companies/:companyId/facturatie"
              element={<ProtectedRoute roles={["admin", "job_coach"]}><CompanyBillingPage /></ProtectedRoute>}
            />

            <Route
              path="/dashboard"
              element={<ProtectedRoute roles={["job_coach"]}><JobCoachDashboardPage /></ProtectedRoute>}
            />
            <Route
              path="/coachees/:coacheeId"
              element={<ProtectedRoute roles={["job_coach", "admin"]}><CoacheeDetailPage /></ProtectedRoute>}
            />

            <Route
              path="/settings"
              element={<ProtectedRoute roles={["job_coach"]}><JobCoachSettingsPage /></ProtectedRoute>}
            />

            {/* Coachee — new menu structure */}
            <Route
              path="/tests"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeTestsPage /></ProtectedRoute>}
            />
            <Route
              path="/tests/iq"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeIQTestPage /></ProtectedRoute>}
            />
            <Route
              path="/tests/werkwaarde"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeWorkValuesPage /></ProtectedRoute>}
            />
            <Route
              path="/tests/loopbaan"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeCareerValuesPage /></ProtectedRoute>}
            />
            <Route
              path="/tests/competentie"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeCompetencePage /></ProtectedRoute>}
            />
            <Route
              path="/tests/beroepen"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeProfessionPage /></ProtectedRoute>}
            />
            <Route
              path="/tests/conflict"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeConflictTestPage /></ProtectedRoute>}
            />
            <Route
              path="/cv"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeCVPage /></ProtectedRoute>}
            />
            <Route
              path="/profielen"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeProfilesPage /></ProtectedRoute>}
            />
            <Route
              path="/profielen/breintype"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeBrainTypeProfilePage /></ProtectedRoute>}
            />
            <Route
              path="/profielen/werknemer"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeEmployeeProfilePage /></ProtectedRoute>}
            />
            <Route
              path="/profielen/werkgever"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeEmployerProfilePage /></ProtectedRoute>}
            />
            <Route
              path="/profielen/zoekprofiel"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeSearchProfilePage /></ProtectedRoute>}
            />
            <Route
              path="/profielen/linkedin"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeLinkedinPlanPage /></ProtectedRoute>}
            />
            <Route
              path="/vacatures"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeVacaturesPage /></ProtectedRoute>}
            />
            <Route
              path="/sollicitaties"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeApplicationsListPage /></ProtectedRoute>}
            />
            <Route
              path="/sollicitaties/:letterId"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeCoverLetterPage /></ProtectedRoute>}
            />
            <Route
              path="/cover-letter"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeCoverLetterPage /></ProtectedRoute>}
            />
            <Route
              path="/interview"
              element={<ProtectedRoute roles={["coachee"]}><CoacheeInterviewPage /></ProtectedRoute>}
            />
            <Route
              path="/survey"
              element={<ProtectedRoute roles={["coachee"]}><SurveyPage /></ProtectedRoute>}
            />
            {/* Backwards-compat: legacy /me → /tests, /zoekprofiel → /profielen */}
            <Route path="/me" element={<Navigate to="/tests" replace />} />
            <Route path="/zoekprofiel" element={<Navigate to="/profielen/zoekprofiel" replace />} />

            <Route
              path="/help"
              element={<ProtectedRoute roles={["job_coach", "coachee"]}><HelpPage /></ProtectedRoute>}
            />
            <Route
              path="/mijn-privacy"
              element={<ProtectedRoute roles={["coachee"]}><MyPrivacyPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/verwijderverzoeken"
              element={<ProtectedRoute roles={["admin", "job_coach"]}><DeletionRequestsPage /></ProtectedRoute>}
            />
            <Route path="*" element={<RoleRedirect />} />
          </Routes>
          </AITaskProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
